import { getAimetaNativeBridge, isAimetaAndroidWebView } from '@/utils/platform';

export interface AppVersionInfo {
  packageName: string;
  versionCode: number;
  versionName: string;
}

export interface AppUpdateInfo {
  hasUpdate: boolean;
  updateId?: number;
  versionCode?: number;
  versionName?: string;
  updateContent?: string;
  downloadUrl?: string;
  fileSize?: number;
  fileMd5?: string;
  forceUpdate?: boolean;
  incremental?: boolean;
}

export interface AppUpdateProgressDetail {
  phase:
    | 'idle'
    | 'checking'
    | 'downloading'
    | 'verifying'
    | 'installing'
    | 'done'
    | 'error';
  progress: number;
  bytesDownloaded: number;
  bytesTotal: number;
  speedBytesPerSec: number;
  message: string;
}

/** 读取手机 App 版本（优先 window 注入，其次 JsBridge.getAppVersion） */
export function getAppVersion(): AppVersionInfo | null {
  if (!isAimetaAndroidWebView()) return null;

  const win = window as Window & {
    __AIMETA_APP_VERSION_CODE__?: number;
    __AIMETA_APP_VERSION_NAME__?: string;
  };
  const injectedName = win.__AIMETA_APP_VERSION_NAME?.trim();
  const injectedCode = win.__AIMETA_APP_VERSION_CODE__;
  if (injectedName && injectedCode != null && injectedCode > 0) {
    return {
      packageName: 'com.aimeta.live',
      versionCode: injectedCode,
      versionName: injectedName,
    };
  }

  const bridge = getAimetaNativeBridge();
  if (!bridge?.getAppVersion) return null;
  try {
    return JSON.parse(bridge.getAppVersion()) as AppVersionInfo;
  } catch {
    return null;
  }
}

/** 原生更新页：检查并展示（Android WebView） */
export function presentNativeAppUpdateUi(mode: 'startup' | 'manual'): boolean {
  if (!isAimetaAndroidWebView()) return false;
  const bridge = getAimetaNativeBridge();
  if (!bridge?.presentAppUpdateUi) return false;
  try {
    const raw = bridge.presentAppUpdateUi(mode);
    const parsed = JSON.parse(raw) as { ok?: boolean };
    return parsed.ok !== false;
  } catch (e) {
    console.warn('[appUpdate] presentAppUpdateUi failed', e);
    return false;
  }
}

export async function checkAppUpdate(): Promise<AppUpdateInfo | null> {
  if (!isAimetaAndroidWebView()) return null;
  const bridge = getAimetaNativeBridge();
  if (!bridge?.checkAppUpdate) return null;
  try {
    const raw = bridge.checkAppUpdate();
    return JSON.parse(raw) as AppUpdateInfo;
  } catch (e) {
    console.warn('[appUpdate] checkAppUpdate failed', e);
    return null;
  }
}

export function startAppUpdate(info: AppUpdateInfo): boolean {
  const bridge = getAimetaNativeBridge();
  if (!bridge?.startAppUpdate) return false;
  try {
    const raw = bridge.startAppUpdate(JSON.stringify(info));
    const parsed = JSON.parse(raw) as { ok?: boolean };
    return parsed.ok !== false;
  } catch (e) {
    console.warn('[appUpdate] startAppUpdate failed', e);
    return false;
  }
}

export function cancelAppUpdate(): void {
  getAimetaNativeBridge()?.cancelAppUpdate?.();
}

export function exitNativeApp(): void {
  getAimetaNativeBridge()?.exitApp?.();
}

export function onAppUpdateProgress(
  listener: (detail: AppUpdateProgressDetail) => void,
): () => void {
  const handler = (event: Event) => {
    listener((event as CustomEvent<AppUpdateProgressDetail>).detail);
  };
  window.addEventListener('aimeta-app-update-progress', handler as EventListener);
  return () => {
    window.removeEventListener(
      'aimeta-app-update-progress',
      handler as EventListener,
    );
  };
}

export function formatByteSize(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** i;
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
